import {useClaims} from "Hooks/api/useClaims";
import {useEffect, useMemo, useState} from "react";
import {useSelector} from "react-redux";
import {PROMO_PREFIXES} from "Constants";
import extendedDayjs from "Helpers/dayjs";
import {_useUnclaimedPromotions} from "Hooks/api/usePromotions";
import {
    GAMBIT_DOUBLE_WHEEL_MODAL_ID,
    GAMBIT_MATCH_THREE_MODAL_ID,
    GAMBIT_PENALTY_SHOOTOUT_MODAL_ID,
    GAMBIT_SINGLE_WHEEL_MODAL_ID,
    GAMBIT_STEP_CLIMB_MODAL_ID,
    IS_DEV,
    IS_DOUBLE_WHEEL_ENABLED,
    IS_MATCH_THREE_ENABLED,
    IS_PENALTY_SHOOTOUT_ENABLED, IS_QA3,
    IS_SINGLE_WHEEL_ENABLED,
    IS_STEP_CLIMB_ENABLED,
    SHARED_CDN_PATH
} from "Setup/config";
import {sleep} from "Helpers/async";
import useGames from "Hooks/useGames";
import {makeStyles} from "@material-ui/core";
import {sendLogglyEvent} from "Helpers/loggly";
import {sendGA4Event} from "Helpers/gaEvents";
// import {activeTheme} from "Redux/reducers/uiReducer";
// import {useGomTheme} from "Hooks/ui/useGomTheme";

export const MATCH_THREE_CARDS = "MatchThreeCards";
export const DOUBLE_WHEEL = "DoubleWheel";
export const SINGLE_WHEEL = "SingleWheel";
export const PENALTY_SHOOTOUT = "PenaltyShootout";
export const STEP_CLIMB = "StepClimb";

// In dev, the art can come from a local share-images checkout instead of the CDN, to try a skin
// before it's uploaded: set localStorage.gambitAssetsBase to the origin serving it.
const devAssetsBase = () => {
    if (!IS_DEV) return null;

    try {
        return localStorage.getItem("gambitAssetsBase");
    } catch (e) {
        return null;
    }
};

const GAMIFICATION_ASSETS_PATH = `${devAssetsBase() ?? SHARED_CDN_PATH}/Gamification/%GAME%/`;
const GAMIFICATION_IMAGE_PATH = `${GAMIFICATION_ASSETS_PATH}webp/`;

const setAssetsPathForGamification = (game) => GAMIFICATION_ASSETS_PATH.replace('%GAME%', game);
const setImagesPathForGamification = (game) => GAMIFICATION_IMAGE_PATH.replace('%GAME%', game);

const gambitGameConfigSkins = {
    [MATCH_THREE_CARDS]: "SantasWorkshop",
    [DOUBLE_WHEEL]: "DoubleWheel",
    [SINGLE_WHEEL]: "SingleWheel",
    [PENALTY_SHOOTOUT]: "PenaltyShootout",
    [STEP_CLIMB]: "TrickOrTreat",
};

const useDefaultStyles = makeStyles((theme) => {
    const settings = theme.overrides.DailyDealV2Modal;

    return {
        root: {
            display: "flex",
            flexDirection: "column",
            maxWidth: "100%",
            margin: "0 auto",
            height: '100%',
            position: "relative",
            backgroundSize: "cover",
            backgroundPosition: "center top",
            minHeight: '600px',

            [theme.breakpoints.up('sm')]: {
                height: `min(90vh, 720px)`,
            }
        },
        paper: {
            maxWidth: 1080,
            color: settings.textColor,
            backgroundSize: "cover",
            border: `10px solid ${settings.borderColor}`,

            '& .bg--bonus-money-strapline': {
                color: "white"
            }
        }
    }
});

const usePenaltyShootoutGameSpecificStyles = makeStyles((theme) => ({
    root: {
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundColor: "black",
        backgroundRepeat: "no-repeat",

        [theme.breakpoints.down(640)]: {
            minHeight: "unset",
            maxHeight: "100%",
            backgroundSize: "215% 95%",
            backgroundPosition: "center -20px",
        },
    },

    paper: {
        containerName: "gambitGame",
        containerType: "inline",
        [theme.breakpoints.down(640)]: {
            height: "min(90vh, 667px)",
            width: "min(90vw, 375px)",
        }
    }
}), {index: 10});

// The game sizes itself to fit the window in its art's shape (see StepClimb/Components/Stage),
// so the modal shrink-wraps it. The margin is the one Stage allows for.
const useStepClimbGameSpecificStyles = makeStyles(() => ({
    root: {
        backgroundColor: "black",
        backgroundPosition: "center",
        height: "auto",
        minHeight: "unset",
    },

    paper: {
        width: "auto",
        maxWidth: "none",
        margin: 16,
        maxHeight: "calc(100% - 32px)",
    }
}), {index: 10});

const useSingleWheelGameSpecificStyles = makeStyles((theme) => ({
    root: {
        backgroundPosition: "center bottom",
    },
}), {index: 10});

const getGambitGameConfig = (configId) => {
    const defaultSettings = {
        id: configId,
        audioEnabled: false,
        videoBackgroundEnabled: false,
        setGameClaimOnPlay: true,
        gameClaimName: configId,
        desktopBackground: 'desktop',
        mobileBackground: 'mobile',
        useDefaultStyles,
        useGameSpecificStyles: () => {
        },
    };

    const configs = {
        [MATCH_THREE_CARDS]: {
            enabled: IS_MATCH_THREE_ENABLED,
            audioEnabled: true,
            mastergroupBonus: PROMO_PREFIXES.GAMBIT_MATCH_THREE_GAME_MASTERGROUP,
            prizeBonus: PROMO_PREFIXES.GAMBIT_MATCH_THREE_GAME_PRIZE,
            prizeSymbols: ["❄️", "🎄", "🎅"],
            modalId: GAMBIT_MATCH_THREE_MODAL_ID
        },
        [DOUBLE_WHEEL]: {
            enabled: IS_DOUBLE_WHEEL_ENABLED,
            mastergroupBonus: PROMO_PREFIXES.GAMBIT_DOUBLE_WHEEL_GAME_MASTERGROUP,
            prizeBonus: PROMO_PREFIXES.GAMBIT_DOUBLE_WHEEL_GAME_PRIZE,
            modalId: GAMBIT_DOUBLE_WHEEL_MODAL_ID
        },
        [SINGLE_WHEEL]: {
            enabled: IS_SINGLE_WHEEL_ENABLED,
            mastergroupBonus: PROMO_PREFIXES.GAMBIT_SINGLE_WHEEL_GAME_MASTERGROUP,
            prizeBonus: PROMO_PREFIXES.GAMBIT_SINGLE_WHEEL_GAME_PRIZE,
            modalId: GAMBIT_SINGLE_WHEEL_MODAL_ID,
            useGameSpecificStyles: useSingleWheelGameSpecificStyles,
            variants: {
                "CaptainCodWheel": {
                    audioEnabled: true,
                    videoBackgroundEnabled: true
                },
                "WoollySafariWheel": {},
                // Drawn to fill the modal rather than at the first wheels' fixed size: a big sign,
                // with the wheel just below it taking the rest of the modal's height
                // (min(90vh, 720px)), up to 480px. The prizes are the bonus's segmentValues, in
                // the skin's Lilita One with a glow in each segment's neon colour.
                "TropiglowWheel": {
                    wheelImage: "wheel",
                    labels: {
                        radius: 26.6,
                        size: 9.4,
                        maxCharacters: 3,
                        font: {family: "Lilita One", file: "LilitaOne.woff2"},
                        color: "#ffffff",
                        outline: "#22053a",
                        glows: ["#ff2d95", "#9b3dff", "#00c8ff", "#5cff3a"],
                    },
                    layout: {
                        signHeight: 220,
                        wheelSize: "min(480px, calc(min(90vh, 720px) - 250px))",
                        top: {xs: 16, sm: -8},
                        pointer: {maxWidth: 72, scale: 0.75},
                    },
                },
                // Made from the Trolley Cash Dash client's own art and music. Its sign is the game's
                // wide, short logo. The wheel is at most 480px, smaller on short windows, and the
                // logo is held to the frame's width on phones. The prizes are the bonus's
                // segmentValues, in the game's gold digits.
                "TrolleyCashDashWheel": {
                    audioEnabled: true,
                    wheelImage: "wheel",
                    labels: {
                        radius: 30.6,
                        size: 10.6,
                        maxCharacters: 2,
                        digits: true,
                        overlap: 0.08,
                    },
                    layout: {
                        signHeight: 120,
                        signMaxWidth: "100%",
                        wheelSize: "min(480px, calc(min(90vh, 720px) - 200px))",
                        top: {xs: 80, sm: 44},
                        pointer: {maxWidth: 72, scale: 0.75},
                    },
                },
            }
        },
        [PENALTY_SHOOTOUT]: {
            enabled: IS_PENALTY_SHOOTOUT_ENABLED,
            audioEnabled: true,
            mastergroupBonus: PROMO_PREFIXES.GAMBIT_PENALTY_SHOOTOUT_GAME_MASTERGROUP,
            prizeBonus: PROMO_PREFIXES.GAMBIT_PENALTY_SHOOTOUT_GAME_PRIZE,
            modalId: GAMBIT_PENALTY_SHOOTOUT_MODAL_ID,
            mobileBackground: 'desktop',
            useGameSpecificStyles: usePenaltyShootoutGameSpecificStyles,
        },
        [STEP_CLIMB]: {
            enabled: IS_STEP_CLIMB_ENABLED,
            audioEnabled: true,
            mastergroupBonus: PROMO_PREFIXES.GAMBIT_STEP_CLIMB_GAME_MASTERGROUP,
            prizeBonus: PROMO_PREFIXES.GAMBIT_STEP_CLIMB_GAME_PRIZE,
            modalId: GAMBIT_STEP_CLIMB_MODAL_ID,
            useGameSpecificStyles: useStepClimbGameSpecificStyles,
        },
    }

    const chosenConfig = configs?.[configId];

    if (chosenConfig) {
        return {
            ...defaultSettings,
            ...chosenConfig
        };
    }

    return null;
}

export const useGambitGameV2 = (game = null) => {
    const isTestMode = IS_DEV || IS_QA3;

    // const {appliedTheme} = useGomTheme();
    const TODAY = extendedDayjs().format("DD-MM-YYYY");
    const GAMBIT_GAMES_CLAIM = "GambitGamesState";
    const GAME_CONFIG = getGambitGameConfig(game);
    const GAME_CLAIM_NAME = GAME_CONFIG?.gameClaimName ?? null;

    // Defined first: the memos below call it while rendering.
    const sendLogglyEventForGambitGame = (key) => {
        sendLogglyEvent(`Gambit${game}`, `Error parsing ${key} from Bede Bonus`);
    };

    // Useful dev state to mock the initial deposit screen
    const [canPlayGambitGame_DEV, setCanPlayGambitGame_DEV] = useState(false);
    const [isMasterGroupBankingInProgress, setIsMasterGroupBankingInProgress] = useState(false);
    const [showEndGameMessage, setShowEndGameMessage] = useState(false);
    const {unclaimedPromotions, unclaimedPromotionsLoaded} = _useUnclaimedPromotions();
    const [isAudioPlaying, setIsAudioPlaying] = useState(true);

    // Banking opens over the game. If the player closes it without depositing, the
    // deposit button has to work again (a loading button ignores clicks).
    const isBankingOpen = useSelector(state => state.ui.modals.queue.some(modal => modal.modalId === "Banking"));
    useEffect(() => {
        if (!isBankingOpen) setIsMasterGroupBankingInProgress(false);
    }, [isBankingOpen]);

    const toggleAudio = () => {
        setIsAudioPlaying(!isAudioPlaying);
    };

    const {_make, _update, playerClaimsLoaded, playerClaims} = useClaims();
    const {makePlayerClaim} = _make();
    const {updatePlayerClaim} = _update();
    const {findGameByCatalogueGameId} = useGames();

    const launchMasterGroupBanking = async () => {
        setIsMasterGroupBankingInProgress(true);
        captureEventForGambitGame('Launched banking');

        if (isTestMode) {
            await sleep(3000);
            setCanPlayGambitGame_DEV(true);
        }
    };

    const mastergroupBonus = useMemo(() => {
        if (!GAME_CONFIG) return null;
        if (!unclaimedPromotionsLoaded) return false;

        return unclaimedPromotions.find(promo => {
            try {
                const internalName = promo.internalName.toUpperCase();
                return internalName.startsWith(GAME_CONFIG.mastergroupBonus);
            } catch (err) {
                console.log(err);
            }
        })
    }, [GAME_CONFIG, unclaimedPromotions, unclaimedPromotionsLoaded]);

    const gameSkinId = useMemo(() => {
        if (!mastergroupBonus) return gambitGameConfigSkins?.[game];

        const KEY = "SkinId";

        try {
            if (mastergroupBonus?.customFields) {

                const skinId = mastergroupBonus.customFields.find(cf => cf?.key?.toLowerCase() === KEY.toLowerCase());
                if (skinId) {
                    // console.log("appliedTheme", appliedTheme);
                    // if (appliedTheme?.layout?.brandId !== "butlers-uk") {
                    //     return "WoollySafariWheel";
                    // }
                    return skinId?.value;
                }
            }
        } catch (e) {
            sendLogglyEventForGambitGame(KEY);
        }

        return gambitGameConfigSkins?.[game];
        // }, [mastergroupBonus, appliedTheme]);
    }, [mastergroupBonus]);

    const prizeBonus = useMemo(() => {
        if (!GAME_CONFIG) return null;
        if (!unclaimedPromotionsLoaded) return false;

        return unclaimedPromotions.find(p => p?.internalName?.toUpperCase().includes(GAME_CONFIG.prizeBonus));
    }, [GAME_CONFIG, unclaimedPromotions, unclaimedPromotionsLoaded])

    const setGamePlayedClaim = () => {
        if (isTestMode) return;
        if (!GAME_CONFIG.setGameClaimOnPlay) return;
        if (!playerClaimsLoaded) return;

        const existingClaim = playerClaims.find(claim => {
            return claim.type === GAMBIT_GAMES_CLAIM;
        });

        if (existingClaim) {
            try {
                let parsedClaimValue = JSON.parse(existingClaim.value);

                // Tidy up the claim. If a game has an old date, delete it.
                for (let gameClaimName in parsedClaimValue) {
                    if (parsedClaimValue[gameClaimName]?.lastPlayed !== TODAY)
                        delete parsedClaimValue[gameClaimName];
                }

                if (!parsedClaimValue?.[GAME_CLAIM_NAME]) {
                    parsedClaimValue[GAME_CLAIM_NAME] = {};
                }

                parsedClaimValue[GAME_CLAIM_NAME].lastPlayed = TODAY;

                updatePlayerClaim(existingClaim.id, JSON.stringify(parsedClaimValue));
            } catch (e) {
                console.log(e);
            }
        } else {
            let claimValue = {};
            claimValue[GAME_CLAIM_NAME] = {};
            claimValue[GAME_CLAIM_NAME].lastPlayed = TODAY;

            makePlayerClaim(GAMBIT_GAMES_CLAIM, JSON.stringify(claimValue));
        }
    };

    const canViewGame = useMemo(() => {
        if (!GAME_CONFIG || !GAME_CONFIG?.enabled) return null;
        if (!playerClaimsLoaded) return false;
        if (!mastergroupBonus || !prizeBonus) return false;

        const existingClaim = playerClaims.find(claim => {
            return claim.type === GAMBIT_GAMES_CLAIM;
        });

        if (existingClaim?.value) {
            // This runs on every page through usePromotions, so a bad claim value mustn't throw.
            try {
                const parsedClaimValue = JSON.parse(existingClaim.value);
                if (parsedClaimValue?.[GAME_CLAIM_NAME]?.lastPlayed === TODAY) return false;
            } catch (e) {
                sendLogglyEvent(`Gambit${game}`, `Error parsing the ${GAMBIT_GAMES_CLAIM} player claim`);
            }
        }

        return true;
    }, [GAME_CONFIG, mastergroupBonus, prizeBonus, playerClaims, playerClaimsLoaded]);

    const canPlayGame = useMemo(() => {
        if (!canViewGame) return false;

        return mastergroupBonus && !mastergroupBonus?.canBeClaimed && prizeBonus?.canBeClaimed;
    }, [GAME_CONFIG, canViewGame, mastergroupBonus, prizeBonus]);

    const winningGameDataFromCustomFields = useMemo(() => {
        if (!prizeBonus) return null;

        if (prizeBonus?.customFields) {
            const gameId = prizeBonus.customFields.find(cf => cf.key === "GameIdLaunchAfterClaim");
            if (gameId?.value) {
                return findGameByCatalogueGameId(gameId.value);
            }
        }

        return null;
    }, [prizeBonus]);

    const getVariantProperty = (prop) => {
        const defaultValue = GAME_CONFIG?.[prop] ?? null;

        if (!GAME_CONFIG?.variants) return defaultValue;
        if (!gameSkinId) return defaultValue;
        if (!GAME_CONFIG?.variants?.[gameSkinId]) return defaultValue;

        return GAME_CONFIG?.variants?.[gameSkinId]?.[prop] ?? defaultValue;
    }


    const audioEnabled = useMemo(() => {
        if (!mastergroupBonus) return null;
        const KEY = "audioEnabled";

        if (mastergroupBonus?.customFields) {
            const audio = mastergroupBonus.customFields.find(cf => cf?.key?.toLowerCase() === KEY.toLowerCase());
            if (audio) {
                return audio?.value.toLowerCase() === "true";
            } else {
                return getVariantProperty('audioEnabled');
            }
        }

        return null;
    }, [mastergroupBonus]);

    const videoBackgroundEnabled = useMemo(() => {
        if (!mastergroupBonus) return null;
        const KEY = "videoBgEnabled";

        if (mastergroupBonus?.customFields) {
            const videoBg = mastergroupBonus.customFields.find(cf => cf?.key?.toLowerCase() === KEY.toLowerCase());
            if (videoBg) {
                return videoBg?.value.toLowerCase() === "true";
            } else {
                return getVariantProperty('videoBackgroundEnabled');
            }
        }

        return null;
    }, [mastergroupBonus]);

    const captureEventForGambitGame = (eventMsg) => {
        try {
            const CATEGORY = "Gambit Game";
            const sharedData = {
                game_type: game,
                skin: gameSkinId,
            }

            sendLogglyEvent(CATEGORY, eventMsg, {
                ...sharedData
            });

            sendGA4Event(CATEGORY, {
                ...sharedData,
                event_msg: eventMsg,
            });
        }
        catch(e){
            console.error(e);
        }
    }


    const assetsPath = useMemo(() => setAssetsPathForGamification(gameSkinId), [gameSkinId]);
    const imagesPath = useMemo(() => setImagesPathForGamification(gameSkinId), [gameSkinId]);

    const gameConfig = {
        assetsPath,
        imagesPath,
        ...GAME_CONFIG,
        hasAudio: audioEnabled,
        videoBackgroundEnabled
    }

    return {
        isTestMode,
        isAudioPlaying,
        toggleAudio,
        gameConfig,
        setGamePlayedClaim,
        setCanPlayGambitGame_DEV,
        canPlayGambitGame: isTestMode ? canPlayGambitGame_DEV : canPlayGame,
        canViewGambitGame: canViewGame,
        gambitGamePrizeBonus: prizeBonus,
        gambitGameMastergroupBonus: mastergroupBonus,
        gameSkinId,
        isMasterGroupBankingInProgress,
        launchMasterGroupBanking,
        showEndGameMessage,
        setShowEndGameMessage,
        winningGameDataFromCustomFields,
        sendLogglyEventForGambitGame,
        captureEventForGambitGame
    };
};